import { useStore } from '@nanostores/react';
import { useCallback, useEffect, useState, type UIEvent, type ChangeEvent } from 'react';
import { toast } from 'react-toastify';
import { DialogHeader, DialogActions, DialogButton } from '~/components/ui/Dialog';
import { Input } from '~/components/ui/Input';
import { LoadingDots } from '~/components/ui/LoadingDots';
import { removeDomain } from '~/lib/.client/api/netlify';
import { fetchSiteInfo } from '~/lib/.client/api/sites';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { supabaseStore } from '~/lib/stores/supabase';
import { userStore } from '~/lib/stores/user';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { logger } from '~/utils/logger';
import { withSpinner } from '~/utils/withSpinner';

type SiteInfo = {
  kind: 'static-hosting' | 'netlify';
  site_url: string;
  is_custom_domain: boolean;
};

interface DeleteChatDialogContentProps {
  projectId?: string;
  itemDescription?: string;
  onDeleteItem?: (event: UIEvent) => Promise<void>;
  onClose?: (event: UIEvent) => void;
}

export const DeleteChatDialogContent = (props: DeleteChatDialogContentProps) => {
  const { projectId, itemDescription, onDeleteItem, onClose } = props;

  const featureFlags = useStore(userStore.featureFlags);
  const supabaseStatus = useStore(supabaseStore.supabaseStatus);

  const [fetchingSiteInfo, setFetchingSiteInfo] = useState(true);
  const [removingChat, setRemovingChat] = useState(false);
  const [siteInfo, setSiteInfo] = useState<SiteInfo | null>(null);
  const [enteredDomainName, setEnteredDomainName] = useState('');

  const showDatabaseWarning = featureFlags.supabaseManagement && supabaseStatus === 'CONFIGURED';

  useEffect(() => {
    const innerFetchSiteInfo = async () => {
      if (!projectId) {
        toast.error('No project selected');
        return;
      }

      await withSpinner({
        action: async () => {
          try {
            const data = await fetchSiteInfo(projectId);
            setSiteInfo(data);
          } catch (error) {
            toast.error('Failed to load site info');
            logger.error(error);
          }
        },
        onStateUpdate: setFetchingSiteInfo,
      });
    };

    innerFetchSiteInfo();
  }, []);

  const isBoltCustomDomain = siteInfo?.kind === 'static-hosting' && siteInfo?.is_custom_domain;
  const fetchedDomainName = siteInfo ? extractDomainName(siteInfo.site_url) : '';
  const isDomainMatched = enteredDomainName === fetchedDomainName;

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setEnteredDomainName(event.target.value.trim().toLowerCase());
  }, []);

  const handleDeleteChat = useCallback(
    async (event: UIEvent) => {
      await withSpinner({
        action: async () => {
          try {
            if (showDatabaseWarning) {
              sendAnalyticsEvent({
                action: AnalyticsAction.Track,
                payload: {
                  event: AnalyticsTrackEvent.DatabaseDeleted,
                },
              });
            }

            if (isBoltCustomDomain && isDomainMatched && projectId) {
              await removeDomain(projectId, fetchedDomainName);
            }

            await onDeleteItem?.(event);
          } catch (error) {
            toast.error('Failed to remove the chat');
            logger.error(error);
          }
        },
        onStateUpdate: setRemovingChat,
      });
    },
    [isBoltCustomDomain, isDomainMatched, fetchedDomainName, projectId, onDeleteItem],
  );

  const dialogDescription = (
    <>
      <p className="mt-3">
        You are about to delete <strong>{itemDescription}</strong>.
      </p>
      {fetchingSiteInfo ? (
        <LoadingDots className="mt-3 justify-start" text="Checking the conditions for deleting the chat" />
      ) : (
        <>
          <p className="mt-3">
            Are you sure you want to delete this chat?
            {showDatabaseWarning && <span> This will also delete your database. This action is irreversible.</span>}
          </p>

          {isBoltCustomDomain && (
            <>
              <p className="mt-3">
                This project is associated with the domain{' '}
                <span className="font-medium text-bolt-ds-textPrimary">{fetchedDomainName}</span>
              </p>
              <p className="mt-1">
                To confirm that you want to detach this domain from the project, please type the name of the domain in
                the input below
              </p>

              <Input
                className="mt-2 mx-0.5 mb-0.5"
                placeholder="Enter domain name to confirm"
                value={enteredDomainName}
                onChange={handleChange}
                aria-label="Enter domain name to confirm deletion"
              />
            </>
          )}
        </>
      )}
    </>
  );

  return (
    <>
      <DialogHeader title="Delete Chat?" description={dialogDescription} />

      <DialogActions>
        <DialogButton variant="secondary" onClick={onClose}>
          Cancel
        </DialogButton>
        <DialogButton
          color="danger"
          onClick={handleDeleteChat}
          disabled={fetchingSiteInfo || (isBoltCustomDomain && !isDomainMatched)}
          loading={removingChat}
        >
          Delete
        </DialogButton>
      </DialogActions>
    </>
  );
};

const PROTOCOL_REGEX = /^https?:\/\//;

const extractDomainName = (url: string): string => {
  try {
    // if the URL doesn't start with a protocol, add https://
    const urlWithProtocol = PROTOCOL_REGEX.test(url) ? url : `https://${url}`;

    const parsedUrl = new URL(urlWithProtocol);

    return parsedUrl.hostname.toLowerCase();
  } catch {
    // if URL parsing fails, try to extract domain manually
    const cleaned = url.replace(PROTOCOL_REGEX, '').split('/')[0];

    return cleaned.toLowerCase();
  }
};
